import {
  ChangeEvent,
  FocusEvent,
  forwardRef,
  InputHTMLAttributes,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useState,
} from 'react';

import { ErrorMessage } from './ErrorMessage';

interface IAutocompleteInputProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'inputMode' | 'defaultValue' | 'value' | 'onChange'
  > {
  readonly label?: string;

  readonly onSuggestionClick: (value: string | undefined) => void;
  readonly suggestions: ReadonlyArray<string>;
  readonly suggestionRenderer: (suggestion: string) => ReactNode;
  readonly errors?: ReadonlyArray<string>;
  readonly value?: string | undefined;
  readonly onChange: (value: string) => void;
}

export const AutocompleteSelect = forwardRef<
  HTMLInputElement,
  IAutocompleteInputProps
>(function AutocompleteSelect(props, ref) {
  const {
    errors = [],
    label,
    type,
    suggestions,
    onSuggestionClick,
    onKeyDown,
    onChange,
    required,
    value,
    suggestionRenderer,
    readOnly,
    disabled,
    ...inputProps
  } = props;
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [selectedValue, setSelectedValue] = useState<string | undefined>(value);

  const onKeyDownHandler = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key?.toLowerCase() === 'escape') {
        setShowSuggestions(false);
      }

      if (
        ['backspace', 'delete'].includes(event.key?.toLowerCase()) &&
        selectedValue &&
        inputValue === ''
      ) {
        setSelectedValue(undefined);
      }

      onKeyDown?.(event);
    },
    [inputValue, onKeyDown, selectedValue],
  );

  const onChangeHandler = useCallback(
    (value: string) => {
      setInputValue(value);
      onChange?.(value);
    },
    [onChange],
  );

  const onSuggestionClickHander = useCallback(
    (suggestion: string | undefined) => {
      onSuggestionClick(suggestion);
      onChangeHandler('');
      setSelectedValue(suggestion);
      setShowSuggestions(false);
    },
    [onChangeHandler, onSuggestionClick],
  );

  const onWrapperBlur = useCallback((event: FocusEvent) => {
    if (!event.relatedTarget) {
      setShowSuggestions(false);
    }
  }, []);

  const onWrapperFocus = useCallback(() => {
    setShowSuggestions(true);
  }, []);

  const inputType = (type === 'number' ? 'text' : type) ?? 'text';
  const inputMode = type === 'number' ? 'numeric' : undefined;

  return (
    <div
      className="pmi_web_inputs__wrapper pmi_web_inputs__autocomplete"
      onBlur={onWrapperBlur}
      onFocus={onWrapperFocus}
      data-disabled={readOnly || disabled}
    >
      <label
        className={'pmi_web_inputs__inputlabel'}
        data-errors={errors.length > 0}
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <div className="pmi_web_inputs__input_wrapper">
          {selectedValue && (
            <div className="pmi_web_inputs__inputselectedvaluewrapper">
              {suggestionRenderer(selectedValue)}
              <button
                onClick={() => onSuggestionClickHander(undefined)}
                title={undefined}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="11"
                  height="11"
                  viewBox="0 0 12 12"
                >
                  <g fill="currentColor">
                    <path
                      d="M10.707,1.293a1,1,0,0,0-1.414,0L6,4.586,2.707,1.293A1,1,0,0,0,1.293,2.707L4.586,6,1.293,9.293a1,1,0,1,0,1.414,1.414L6,7.414l3.293,3.293a1,1,0,0,0,1.414-1.414L7.414,6l3.293-3.293A1,1,0,0,0,10.707,1.293Z"
                      fill="currentColor"
                    ></path>
                  </g>
                </svg>
              </button>
            </div>
          )}
          <input
            {...inputProps}
            type={inputType}
            inputMode={inputMode}
            ref={ref}
            onKeyDown={onKeyDownHandler}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onChangeHandler(event?.target.value)
            }
            value={inputValue}
            data-required={required}
            readOnly={readOnly}
            disabled={disabled}
          />
        </div>
        {label && <span>{label}</span>}
      </label>
      {showSuggestions && suggestions.length > 0 && (
        <ul>
          {suggestions.map((s, index) => (
            <li key={`${index}_${s}`}>
              <button onClick={() => onSuggestionClickHander(s)}>
                {suggestionRenderer(s)}
              </button>
            </li>
          ))}
        </ul>
      )}
      {errors.map(error => (
        <ErrorMessage key={error} message={error} />
      ))}
    </div>
  );
});
